import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseItem } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/lib/agents/db"
import {
  type CmsPermissions, type CmsAccess, type CmsReview, CMS_SECTIONS, CMS_ACCESS_LEVELS, CMS_REVIEW_LEVELS,
  FULL_CMS_ACCESS, NO_CMS_ACCESS, parseCmsPermissions,
} from "@/pages/dev/smart-cms/shared/permissions"
import { parseAssistantPermissions } from "@/lib/assistant/permissions"
import { zodResolver } from "@hookform/resolvers/zod"
import { Shield, Plus, Eye, Pencil, Trash2, Share2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"

export const roles_crud = {
  title: "Role",
  route: "/settings/roles/:id",
  component,
  group: "settings",
  resource: "roles",
  icon: Shield,
  action: "read",
} satisfies PageConfig

const PermissionSchema = z.object({
  resource: z.string(),
  create: z.boolean(),
  read: z.boolean(),
  update: z.boolean(),
  delete: z.boolean(),
  share: z.boolean(),
})

const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  permissions: z.array(PermissionSchema),
  cms_permissions: z.any().optional(),
})

type FormData = z.infer<typeof FormSchema>

const APP_RESOURCES = [
  { id: 'agents', label: 'Agents' },
  { id: 'jobs', label: 'Jobs' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'hitl', label: 'Moderation' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'messages', label: 'Messages' },
  { id: 'classifications', label: 'Classifications' },
  { id: 'forms', label: 'Forms' },
  { id: 'riskcategories', label: 'Risk Categories' },
  { id: 'simulator', label: 'Simulator' },
  { id: 'agentchat', label: 'Agents Chat' },
  { id: 'collections', label: 'Collections' },
  { id: 'sources', label: 'Data Sources' },
  { id: 'templates', label: 'Templates' },
  { id: 'projects', label: 'Projects' },
  { id: 'teams', label: 'Teams' },
  { id: 'roles', label: 'Roles' },
  { id: 'users', label: 'Users' },
  { id: 'apikeys', label: 'Api Keys' },
  { id: 'models', label: 'Models' },
]

type PermissionAction = 'create' | 'read' | 'update' | 'delete' | 'share'

const PERMISSION_ICONS: Record<PermissionAction, any> = {
  create: Plus,
  read: Eye,
  update: Pencil,
  delete: Trash2,
  share: Share2
}

const defaultPermissions = APP_RESOURCES.map(resource => ({
  resource: resource.id,
  create: false,
  read: false,
  update: false,
  delete: false,
  share: false
}))

export function component() {
  const { data, loading, submit, toastSuccess, navigate, id } = useDatabaseItem("roles")

  // Merge existing permissions with default permissions for new resources
  const mergePermissions = (existingPermissions: any[]) => {
    const existingMap = new Map(existingPermissions.map(p => [p.resource, p]))
    return defaultPermissions.map(defaultPerm =>
      existingMap.get(defaultPerm.resource) || defaultPerm
    )
  }

  // cms_permissions only exists once the Smart CMS roles migration has run
  const { data: cmsColumnExists } = useQuery({
    queryKey: ["roles-cms-column"],
    queryFn: async () => !(await supabase.from("roles").select("cms_permissions").limit(1)).error,
    staleTime: Infinity,
  })
  // assistant_permissions only exists once the assistant migration has run
  const { data: assistantColumnExists } = useQuery({
    queryKey: ["roles-assistant-column"],
    queryFn: async () => !(await supabase.from("roles").select("assistant_permissions" as any).limit(1)).error,
    staleTime: Infinity,
  })

  const legacyCms = (perms: any[]): CmsPermissions =>
    perms.some(p => p?.resource === "dashboard" && p?.read) ? FULL_CMS_ACCESS : NO_CMS_ACCESS

  const transformedData = data ? {
    ...data,
    permissions: mergePermissions(data.permissions || []) as typeof defaultPermissions,
    cms_permissions: (() => {
      const cms = parseCmsPermissions(data.cms_permissions) ?? legacyCms(data.permissions || [])
      // The assistant settings live in their own column once it exists; show those
      const assistant = parseAssistantPermissions((data as any).assistant_permissions)
      return assistant ? { ...cms, agent: assistant } : cms
    })(),
  } : {
    name: "",
    description: "",
    permissions: defaultPermissions,
    cms_permissions: NO_CMS_ACCESS,
  }

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: transformedData,
    values: transformedData,
  })

  const permissions = form.watch('permissions')
  const cms: CmsPermissions = form.watch('cms_permissions') ?? NO_CMS_ACCESS
  const setCms = (next: CmsPermissions) => form.setValue('cms_permissions', next, { shouldDirty: true })

  const handlePermissionChange = (resource: string, action: PermissionAction, value: boolean) => {
    const updatedPermissions = permissions.map(permission =>
      permission.resource === resource
        ? { ...permission, [action]: value }
        : permission
    )
    form.setValue('permissions', updatedPermissions)
  }

  const handleSelectAll = (resource: string, value: boolean) => {
    const updatedPermissions = permissions.map(permission =>
      permission.resource === resource
        ? {
          ...permission,
          create: value,
          read: value,
          update: value,
          delete: value,
          share: value
        }
        : permission
    )
    form.setValue('permissions', updatedPermissions)
  }

  const handleSelectAllPermissions = (value: boolean) => {
    const updatedPermissions = permissions.map(permission => ({
      ...permission,
      create: value,
      read: value,
      update: value,
      delete: value,
      share: value
    }))
    form.setValue('permissions', updatedPermissions)
  }

  async function onSubmit(data: FormData) {
    const { cms_permissions, ...rest } = data
    const row: Record<string, any> = { ...rest }
    if (cmsColumnExists) row.cms_permissions = cms_permissions
    // The same settings drive the app-wide assistant
    if (assistantColumnExists && cms_permissions?.agent) row.assistant_permissions = cms_permissions.agent
    // assistant_permissions isn't in the generated types until they're regenerated after the migration
    await submit(row as any)
    toastSuccess()
    navigate("/settings/roles")
  }

  if (loading) return <CRUDLoader />

  const allPermissionsChecked = permissions.every(p =>
    p.create && p.read && p.update && p.delete && p.share
  )

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="roles" onSuccess={"/settings/roles"} />
          </PageTitle>

          <div className="rgrid">
            <div className="row">
              <div className="rc-6">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-6">
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl><Textarea {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-12">
                <FormField control={form.control} name="permissions" render={() => (
                  <FormItem>
                    <FormLabel>Permissions</FormLabel>
                    <FormControl>
                      <div className="border rounded-lg p-4">
                        <table className="w-full">
                          <thead>
                            <tr>
                              <th className="text-left py-2">Resource</th>
                              <th className="text-center py-2">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center justify-center space-x-2">
                                        <Plus className="h-4 w-4" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Create new items</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </th>
                              <th className="text-center py-2">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center justify-center space-x-2">
                                        <Eye className="h-4 w-4" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>View items</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </th>
                              <th className="text-center py-2">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center justify-center space-x-2">
                                        <Pencil className="h-4 w-4" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Edit items</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </th>
                              <th className="text-center py-2">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center justify-center space-x-2">
                                        <Trash2 className="h-4 w-4" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Delete items</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </th>
                              <th className="text-center py-2">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center justify-center space-x-2">
                                        <Share2 className="h-4 w-4" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Share</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </th>
                              <th className="text-center py-2">
                                <div className="flex items-center justify-center space-x-2">
                                  <Checkbox
                                    id="select-all-permissions"
                                    checked={allPermissionsChecked}
                                    onCheckedChange={(checked) => handleSelectAllPermissions(checked as boolean)}
                                  />
                                  <Label htmlFor="select-all-permissions">Select All</Label>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {APP_RESOURCES.map(resource => {
                              const permission = permissions.find(p => p.resource === resource.id)
                              return (
                                <tr key={resource.id} className="border-t">
                                  <td className="py-2">
                                    <Label className="font-medium">{resource.label}</Label>
                                  </td>
                                  {Object.entries(PERMISSION_ICONS).map(([action, Icon]) => {
                                    const permissionAction = action as PermissionAction
                                    const isChecked = Boolean(permission?.[permissionAction])
                                    return (
                                      <td key={action} className="text-center py-2">
                                        <Checkbox
                                          id={`${resource.id}-${action}`}
                                          checked={isChecked}
                                          onCheckedChange={(checked) => handlePermissionChange(resource.id, permissionAction, checked as boolean)}
                                        />
                                      </td>
                                    )
                                  })}
                                  <td className="text-center py-2">
                                    <div className="flex items-center justify-center space-x-2">
                                      <Checkbox
                                        id={`select-all-${resource.id}`}
                                        checked={permission?.create && permission?.read && permission?.update && permission?.delete && permission?.share}
                                        onCheckedChange={(checked) => handleSelectAll(resource.id, checked as boolean)}
                                      />
                                      <Label htmlFor={`select-all-${resource.id}`}>Select All</Label>
                                    </div>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-12">
                <FormItem>
                  <FormLabel>Smart CMS</FormLabel>
                  {!cmsColumnExists ? (
                    <p className="rounded-lg border p-4 text-sm text-muted-foreground">
                      Smart CMS permissions need the <code>roles.cms_permissions</code> column. Run the Smart CMS
                      roles migration. Until then, roles with Dashboard read have full CMS access.
                    </p>
                  ) : (
                    <div className="space-y-4 rounded-lg border p-4">
                      <table className="w-full">
                        <thead>
                          <tr>
                            <th className="py-2 text-left">Section</th>
                            <th className="py-2 text-left">Access</th>
                          </tr>
                        </thead>
                        <tbody>
                          {CMS_SECTIONS.map(section => (
                            <tr key={section.id} className="border-t">
                              <td className="py-2"><Label className="font-medium">{section.label}</Label></td>
                              <td className="py-2">
                                <Select
                                  value={cms[section.id]}
                                  onValueChange={v => setCms({ ...cms, [section.id]: v as CmsAccess })}
                                >
                                  <SelectTrigger className="w-[320px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {CMS_ACCESS_LEVELS.map(l => (
                                      <SelectItem key={l.id} value={l.id}>
                                        {l.label} <span className="text-muted-foreground">({l.hint})</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                    </div>
                  )}
                </FormItem>
              </div>
              <div className="rc-12">
                <FormItem>
                  <FormLabel>AI assistant</FormLabel>
                  {!cmsColumnExists ? (
                    <p className="rounded-lg border p-4 text-sm text-muted-foreground">
                      Assistant settings need the Smart CMS roles migration.
                    </p>
                  ) : (
                    <div className="space-y-3 rounded-lg border p-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="cms-agent-use"
                          checked={cms.agent.use}
                          onCheckedChange={c => setCms({ ...cms, agent: { ...cms.agent, use: c === true } })}
                        />
                        <Label htmlFor="cms-agent-use">Can use the assistant, on every page</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Label className="w-[220px] text-muted-foreground">Changes need review</Label>
                        <Select
                          value={cms.agent.review}
                          disabled={!cms.agent.use}
                          onValueChange={v => setCms({ ...cms, agent: { ...cms.agent, review: v as CmsReview } })}
                        >
                          <SelectTrigger className="w-[360px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CMS_REVIEW_LEVELS.map(l => (
                              <SelectItem key={l.id} value={l.id}>
                                {l.label} <span className="text-muted-foreground">({l.hint})</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="cms-agent-configure"
                          checked={cms.agent.configure}
                          disabled={!cms.agent.use}
                          onCheckedChange={c => setCms({ ...cms, agent: { ...cms.agent, configure: c === true } })}
                        />
                        <Label htmlFor="cms-agent-configure">Can change team skills, team memory and the document library</Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        The assistant can only do what the user can: it gets tools only for pages and actions this role allows.
                        {!assistantColumnExists && " Saved in the Smart CMS settings until the assistant migration runs."}
                      </p>
                    </div>
                  )}
                </FormItem>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>
}

